import React, { useEffect, useState } from "react";
import Button from "../../../components/common/button/Button";
import { Form, Formik } from "formik";
import Input from "../../../components/common/inputs/Input";
import { postApi } from "../../../services/api/globalApi";
import { successMessage } from "../../../utils/toastify-message/ToastifyMessage";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RxDragHandleDots2 } from "react-icons/rx";

function SortableItem({ item, index, onRemove, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-4 rounded shadow bg-white border flex justify-between items-center gap-3"
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="cursor-grab text-gray-500 hover:text-gray-700"
        >
          <RxDragHandleDots2 size={20} />
        </button>

        {item.image && (
          <img
            src={item.image}
            alt={`${item.name} ${item.family}`}
            className="w-12 h-12 object-cover rounded-full border"
          />
        )}

        <div>
          <p className="font-bold">
            {item.name} {item.family}
          </p>
          <p className="text-sm text-gray-600">
            {item.province} - {item.city}
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          className="text-blue-500 hover:text-blue-700"
          onClick={() => onEdit(index)}
        >
          ویرایش
        </button>
        <button
          type="button"
          className="text-red-500 hover:text-red-700"
          onClick={() => onRemove(index)}
        >
          حذف
        </button>
      </div>
    </div>
  );
}

function AffiliateRankCard({ data, field }) {
  const [items, setItems] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [editIndex, setEditIndex] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const addOrUpdateItem = (values, { resetForm }) => {
    if (editIndex !== null) {
      setItems((prev) =>
        prev.map((item, i) =>
          i === editIndex ? { ...values, id: item.id } : item
        )
      );
      setEditIndex(null);
      resetForm();
    } else {
      const newItem = {
        ...values,
        id: new Date().getTime(),
      };
      setItems((prev) => [...prev, newItem]);
      resetForm();
    }
  };

  const editItem = (index) => {
    setEditIndex(index);
  };

  const cancelEdit = (resetForm) => {
    setEditIndex(null);
    resetForm();
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const saveItems = async () => {
    const path = "admin/publicFields";
    const body = {
      [field]: { affiliates: items, isActive },
    };
    try {
      await postApi({ path, body });
      successMessage("اطلاعات با موفقیت ویرایش شد");
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (data) {
      const withIds = (data.affiliates || []).map((item) => ({
        ...item,
        id: item.id || Math.floor(Math.random() * 100000),
      }));
      setItems(withIds);
      setIsActive(data?.isActive ?? true);
    }
  }, [data]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over?.id);
      setItems((items) => arrayMove(items, oldIndex, newIndex));
    }
  };

  return (
    <div className="w-full py-4 px-4 border-r border-l">
      <Formik
        initialValues={{
          name: editIndex !== null ? items[editIndex]?.name : "",
          family: editIndex !== null ? items[editIndex]?.family : "",
          province: editIndex !== null ? items[editIndex]?.province : "",
          city: editIndex !== null ? items[editIndex]?.city : "",
          image: editIndex !== null ? items[editIndex]?.image : "",
        }}
        enableReinitialize
        onSubmit={addOrUpdateItem}
      >
        {(formik) => (
          <Form>
            <div className="grid gap-6 mb-4 md:grid-cols-2">
              <Input name="name" placeholder="نام" label="نام" type="text" />
              <Input
                name="family"
                placeholder="نام خانوادگی"
                label="نام خانوادگی"
                type="text"
              />
              <Input
                name="province"
                placeholder="استان"
                label="استان"
                type="text"
              />
              <Input name="city" placeholder="شهر" label="شهر" type="text" />
              <Input
                name="image"
                placeholder="آدرس عکس"
                label="آدرس عکس"
                type="text"
              />
            </div>
            <div className="flex flex-col mt-8 gap-y-2">
              <Button
                text={editIndex !== null ? "ویرایش" : "افزودن به لیست"}
                color={"primary"}
                onClick={formik.handleSubmit}
              />
              {editIndex !== null && (
                <Button
                  text={"انصراف"}
                  color={"danger"}
                  onClick={() => cancelEdit(formik.resetForm)}
                />
              )}
            </div>
          </Form>
        )}
      </Formik>

      <div className="mt-8">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-4">
              {items.map((item, index) => (
                <SortableItem
                  key={item.id}
                  item={item}
                  index={index}
                  onRemove={removeItem}
                  onEdit={editItem}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <div className="flex justify-end mt-6">
          <Button text="ذخیره تغییرات" color="success" onClick={saveItems} />
        </div>
      </div>
    </div>
  );
}

export default AffiliateRankCard;
